import {
  ApplicantType,
  type ApplicationFieldPath,
} from "@versatile-credit-apply/omni-apply-client"
import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from "vue"

import { useSectionShouldRender } from "./composition/use-section-should-render"

import {
  AccountLookupFormFactory,
  PrefilledFormFactory,
  StandardFormFactory,
} from "./factories"

import { type FormSection } from "./sections"

export type FormMode =
  // A mode that is used when information is prefilled from a service like "prove"
  | "prefilled"
  // Default Mode
  | "standard"
  | "account-lookup"

const factories: Record<FormMode, DynamicFormFactory> = {
  prefilled: PrefilledFormFactory,
  standard: StandardFormFactory,
  "account-lookup": AccountLookupFormFactory,
}

export type FormStepId =
  | "personal"
  | "address"
  | "contact"
  | "additional"
  | "spouse"

export interface DynamicFormFactoryContext {
  id: FormStepId
  shouldRender: ReturnType<typeof useSectionShouldRender>
}

export interface DynamicFormFactory {
  id: string

  steps: readonly FormStepId[]
  jointSteps: readonly FormStepId[]

  done?: () => string

  sectionsFor(
    applicantType: ApplicantType,
    context: DynamicFormFactoryContext
  ): FormSection[]
}

const getFormFactory = (formMode: FormMode) => factories[formMode]

export const getFormSteps = (mode: FormMode, applicantType: ApplicantType) => {
  const factory = getFormFactory(mode)
  return applicantType === ApplicantType.PRIMARY
    ? factory.steps
    : factory.jointSteps
}

export const getFormStepForField = (
  mode: FormMode,
  target: ApplicationFieldPath
): FormStepId => {
  const factory = getFormFactory(mode)
  const shouldRenderSection = useSectionShouldRender()

  const applicantType = target.startsWith("joint")
    ? ApplicantType.JOINT
    : ApplicantType.PRIMARY

  const steps = applicantType == "joint" ? factory.jointSteps : factory.steps

  for (const step of steps) {
    const sections = factory.sectionsFor(applicantType, {
      id: step,
      shouldRender: shouldRenderSection,
    })

    const section = sections.find((section) =>
      section.fields.find((field) => field.startsWith(target))
    )

    if (section) {
      return step
    }
  }

  throw new Error(
    `Unable to determine correct form step for ${mode} and ${target}`
  )
}

export const useDynamicFormFactory = (
  mode: MaybeRefOrGetter<FormMode>
): ComputedRef<DynamicFormFactory> =>
  computed(() => getFormFactory(toValue(mode)))

export const useDynamicFormSections = (
  applicantType: MaybeRefOrGetter<ApplicantType>,
  mode: MaybeRefOrGetter<FormMode>,
  id: MaybeRefOrGetter<FormStepId>
): ComputedRef<FormSection[]> => {
  const factory = useDynamicFormFactory(mode)
  const shouldRenderSection = useSectionShouldRender()

  return computed(() =>
    factory.value
      .sectionsFor(toValue(applicantType), {
        id: toValue(id),
        shouldRender: shouldRenderSection,
      })
      .filter(shouldRenderSection)
  )
}

export const getFormFactoryDone = (mode: FormMode): string | undefined => {
  const factory = getFormFactory(mode)
  return factory.done ? factory.done() : undefined
}
